import { useQuery } from '@tanstack/react-query'
import { API_CONFIG } from 'config/api'
import { useMemo } from 'react'

type VipTierInfo = {
  label?: string
  imageUrl?: string
  discountRate?: number
}

type VipTierResponse = {
  userAddress?: string
  hasAllocation?: boolean
  currentAllocation?: string
  currentAllocationFormatted?: string
  vipTier?: unknown
  orderlyAccountId?: string | null
  lastUpdated?: string | null
}

type VipTierResult = {
  tier: VipTierInfo | null
  hasAllocation: boolean
  lastUpdated: string | null
}

const getTierInfo = (tier: unknown): VipTierInfo | null => {
  if (!tier) return null

  if (typeof tier === 'object') {
    const value = tier as Record<string, unknown>
    const label = (value.name || value.label || value.tier) as string | undefined
    const imageUrl = (value.badge_url || value.badgeUrl || value.badgeImageUrl || value.image || value.imageUrl) as
      | string
      | undefined
    const discountRate = (value.discountRate || value.discount_rate) as number | undefined

    if (!label && !imageUrl) return null
    return { label, imageUrl, discountRate }
  }

  if (typeof tier === 'string') {
    return { label: tier }
  }

  return null
}

async function fetchVipTier(address: string, signal?: AbortSignal): Promise<VipTierResult> {
  const url = `${API_CONFIG.baseUrl}/orderly/vip-tiers/user/${address.toLowerCase()}`

  const response = await fetch(url, { signal })
  if (!response.ok) {
    throw new Error(response.statusText)
  }
  const json = (await response.json()) as VipTierResponse
  return {
    tier: getTierInfo(json?.vipTier),
    hasAllocation: Boolean(json?.hasAllocation),
    lastUpdated: json?.lastUpdated ?? null,
  }
}

export function useVipTier(address?: string | null) {
  const queryKey = useMemo(() => ['vipTier', address?.toLowerCase() || 'unknown'], [address])
  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => (address ? fetchVipTier(address, signal) : Promise.resolve(null)),
    enabled: Boolean(address),
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: 1,
  })
  return useMemo(
    () => ({
      tier: query.data?.tier ?? null,
      hasAllocation: query.data?.hasAllocation ?? false,
      lastUpdated: query.data?.lastUpdated ?? null,
      isLoading: query.isLoading,
      isFetched: query.isFetched,
      error: query.error ?? null,
    }),
    [query.data, query.isLoading, query.isFetched, query.error]
  )
}
